import { ReactNode, useEffect, useRef } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useAuthStore } from '../../store/authStore'
import type { LocationLike, NavigateFn } from './RequireAuth'

function AuthLoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50" aria-label="Loading">
      <div className="flex flex-col items-center gap-3 text-slate-600">
        <Loader2 className="h-8 w-8 animate-spin" aria-hidden />
        <span>Loading…</span>
      </div>
    </div>
  )
}

type RequireAdminInnerProps = {
  children: ReactNode
  loginPath?: string
  fallbackPath?: string
  location: LocationLike
  /** When set, redirects are done via this fn (useEffect) instead of <Navigate>. Use from app so Router context is correct. */
  navigate?: NavigateFn
}

/**
 * Protects /admin/*: requires customer token + me.is_admin (from GET /auth/me).
 * Uses provided location and optional navigate to avoid Router context issues when the guard
 * runs with a different react-router instance (e.g. shared package).
 */
export function RequireAdminInner({
  children,
  loginPath = '/',
  fallbackPath = '/dashboard',
  location,
  navigate: navigateFn,
}: RequireAdminInnerProps) {
  const hasHydrated = useAuthStore((s) => s._hasHydrated)
  const customer = useAuthStore((s) => s.customer)
  const me = useAuthStore((s) => s.me)
  const didRedirect = useRef(false)

  const authenticated = !!customer?.token && !!customer?.user

  let redirectTo: string | null = null
  let redirectState: unknown = undefined
  if (!authenticated) {
    redirectTo = loginPath
    redirectState = { from: location }
  } else if (me != null && !me.is_admin) {
    redirectTo = fallbackPath
  }

  useEffect(() => {
    if (!redirectTo || !navigateFn || didRedirect.current) return
    didRedirect.current = true
    navigateFn(redirectTo, { replace: true, state: redirectState })
  }, [redirectTo, navigateFn])

  if (!hasHydrated) return <AuthLoadingScreen />
  if (authenticated && me == null) return <AuthLoadingScreen />
  if (redirectTo) {
    if (navigateFn) return <AuthLoadingScreen />
    return <Navigate to={redirectTo} state={redirectState} replace />
  }
  return <>{children}</>
}

type RequireAdminProps = RequireAdminInnerProps & { location?: LocationLike; navigate?: NavigateFn }

/**
 * Protects /admin/*: requires customer token + me.is_admin.
 * When used in an app that shares the same react-router instance, you can omit location/navigate.
 * When used in an app with a separate react-router (e.g. shared package), pass location + navigate from the app so the guard does not call useLocation() or render <Navigate> (avoids "useLocation must be used in a Router" / invariant errors).
 */
export function RequireAdmin({ location: locationProp, navigate, ...rest }: RequireAdminProps) {
  const locationFromRouter = useLocation()
  const location = locationProp ?? locationFromRouter
  return <RequireAdminInner {...rest} location={location} navigate={navigate} />
}
